import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { text } from '../text';
import { hooks } from '../hooks';
import { components } from '../components';
import { theme } from '../constants';
import { useLanguage } from '../context/LanguageContext';
import axiosInstance from '../services/axiosInstance';
import { MdDelete, MdAdd, MdKeyboardArrowUp, MdKeyboardArrowDown } from 'react-icons/md';

const DeleteIcon = MdDelete as any;
const AddIcon = MdAdd as any;
const ArrowUpIcon = MdKeyboardArrowUp as any;
const ArrowDownIcon = MdKeyboardArrowDown as any;

const parseAmt = (s: string) => s.replace(/\D/g, '');
const formatAmt = (s: string) => parseAmt(s).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

export const TravelExpenseForm: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const { state } = useLocation();
  const { t } = useLanguage();
  const task = state?.task;
  // Edit pengajuan draft/pending (termasuk pengajuan bersama dari task lain di grup perjalanan)
  const existing = state?.expense;
  const isEdit = !!existing;
  const isDraftDoc = !isEdit || existing.status === 'draft';
  const dateStr = (d: any) => (d ? String(d).substring(0, 10) : '');

  const [fromLocation, setFromLocation] = useState(existing?.from_location || '');
  const [toLocation, setToLocation] = useState(existing?.to_location || '');
  const [departureDate, setDepartureDate] = useState(dateStr(existing?.departure_date));
  const [returnDate, setReturnDate] = useState(dateStr(existing?.return_date));
  const [items, setItems] = useState<{ cost_type: string; amount: string }[]>(
    existing?.items?.length
      ? existing.items.map((i: any) => ({ cost_type: i.cost_type || '', amount: String(Math.round(parseFloat(String(i.amount)) || 0)) }))
      : [{ cost_type: '', amount: '' }]
  );
  const [isShared, setIsShared] = useState<boolean>(!!existing?.is_shared);
  const [focusIdx, setFocusIdx] = useState<number | null>(null);
  const [costTypes, setCostTypes] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const te = (t as any)?.travelExpenses;
  const tg = (t as any)?.tripGroup || {};
  // Pilihan "dokumen bersama" hanya relevan kalau task asal dokumen tergabung di grup perjalanan
  const inGroup = !!task?.task_group_id;

  useEffect(() => {
    axiosInstance.get('/travel-cost-types')
      .then(r => setCostTypes(r.data))
      .catch(() => {});
  }, []);

  const filteredTypes = (idx: number) => {
    const q = (items[idx]?.cost_type || '').toLowerCase();
    return costTypes.filter(ct => ct.name.toLowerCase().includes(q));
  };

  const updateItem = (idx: number, field: 'cost_type' | 'amount', val: string) => {
    setItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: val };
      return next;
    });
  };

  const addRow = () => setItems(prev => [...prev, { cost_type: '', amount: '' }]);
  const removeRow = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));
  const moveItem = (idx: number, dir: -1 | 1) => {
    setItems(prev => {
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  const total = items.reduce((s, i) => s + (parseInt(parseAmt(i.amount) || '0', 10) || 0), 0);

  const handleSubmit = async (submitStatus: 'draft' | 'pending') => {
    setErrorMsg('');
    if (!fromLocation || !toLocation || !departureDate || !returnDate) {
      setErrorMsg(te?.errorRequired || 'Semua field wajib diisi');
      return;
    }
    const validItems = items.filter(i => i.cost_type && i.amount);
    if (validItems.length === 0) {
      setErrorMsg(te?.errorItems || 'Tambahkan minimal 1 rincian biaya');
      return;
    }
    if (!task?.id && !isEdit) {
      setErrorMsg(te?.errorTask || 'Task tidak ditemukan');
      return;
    }

    const payload = {
      from_location: fromLocation,
      to_location: toLocation,
      departure_date: departureDate,
      return_date: returnDate,
      items: validItems,
      status: submitStatus,
      is_shared: inGroup ? isShared : !!existing?.is_shared,
    };
    setSubmitting(true);
    try {
      if (isEdit) await axiosInstance.put(`/travel-expenses/${existing.id}/submission`, payload);
      else await axiosInstance.post(`/travel-expenses/task/${task.id}`, payload);
      navigate(-1);
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || te?.errorSave || 'Gagal menyimpan pengajuan');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    border: '1px solid #D1D5DB',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 15,
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
    color: theme.colors.mainDark,
  };

  const labelStyle: React.CSSProperties = {
    color: '#6B7280',
    marginBottom: 6,
    display: 'block',
  };

  return (
    <div id="screen" style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <components.Header title={isEdit ? (te?.editTitle || 'Edit Pengajuan Uang Jalan') : (te?.addTitle || 'Pengajuan Uang Jalan')} goBack={true} />

      <main className="container" style={{ marginTop: 60, paddingBottom: 110, height: 'calc(100vh - 60px)', overflowY: 'auto' }}>
        <div style={{ padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {errorMsg && (
            <div style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 14px' }}>
              <text.T14 style={{ color: '#DC2626' }}>{errorMsg}</text.T14>
            </div>
          )}

          {isEdit && existing.status === 'draft' && (
            <div style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 10, padding: '10px 14px' }}>
              <text.T12 style={{ color: '#92400E' }}>{te?.draftNotice || 'Draft — belum diajukan untuk persetujuan.'}</text.T12>
            </div>
          )}

          {inGroup && (
            <components.SharedDocToggle checked={isShared} onChange={setIsShared} label={tg.shareToggle} hint={tg.shareHint} />
          )}

          {/* Route */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <text.T12 style={labelStyle}>{te?.from || 'Berangkat Dari'}</text.T12>
              <input
                value={fromLocation}
                onChange={e => setFromLocation(e.target.value)}
                placeholder="Lokasi asal"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <text.T12 style={labelStyle}>{te?.to || 'Tujuan Ke'}</text.T12>
              <input
                value={toLocation}
                onChange={e => setToLocation(e.target.value)}
                placeholder="Lokasi tujuan"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Dates */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <text.T12 style={labelStyle}>{te?.departure || 'Tanggal Berangkat'}</text.T12>
              <input
                type="date"
                value={departureDate}
                onChange={e => setDepartureDate(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <text.T12 style={labelStyle}>{te?.returnDate || 'Tanggal Kembali'}</text.T12>
              <input
                type="date"
                value={returnDate}
                onChange={e => setReturnDate(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          {/* Cost items */}
          <div>
            <text.T12 style={{ ...labelStyle, marginBottom: 10 }}>{te?.costItems || 'Rincian Biaya'}</text.T12>
            <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'visible' }}>
              <div style={{ backgroundColor: '#F9FAFB', padding: '8px 12px', display: 'flex', borderBottom: '1px solid #E5E7EB' }}>
                <text.T12 style={{ flex: 1, color: '#6B7280', fontWeight: '600' }}>{te?.costType || 'Jenis Biaya'}</text.T12>
                <text.T12 style={{ width: 110, color: '#6B7280', fontWeight: '600' }}>{te?.amount || 'Nominal (Rp)'}</text.T12>
                <div style={{ width: 64 }} />
              </div>

              {items.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #F3F4F6', position: 'relative' }}>
                  <div style={{ flex: 1, padding: '8px 12px', position: 'relative' }}>
                    <input
                      value={item.cost_type}
                      onChange={e => updateItem(idx, 'cost_type', e.target.value)}
                      onFocus={() => setFocusIdx(idx)}
                      onBlur={() => setTimeout(() => setFocusIdx(null), 150)}
                      placeholder="Ketik atau pilih..."
                      style={{ width: '100%', border: 'none', outline: 'none', fontSize: 14, padding: 0, fontFamily: 'inherit' }}
                    />
                    {focusIdx === idx && filteredTypes(idx).length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff',
                        border: '1px solid #E5E7EB', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                        zIndex: 100, maxHeight: 160, overflowY: 'auto',
                      }}>
                        {filteredTypes(idx).map((ct: any) => (
                          <div
                            key={ct.id}
                            onMouseDown={() => updateItem(idx, 'cost_type', ct.name)}
                            style={{ padding: '10px 14px', fontSize: 14, cursor: 'pointer', borderBottom: '1px solid #F3F4F6' }}
                          >
                            {ct.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ width: 110, padding: '8px 12px' }}>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatAmt(item.amount)}
                      onChange={e => updateItem(idx, 'amount', parseAmt(e.target.value))}
                      placeholder="0"
                      style={{ width: '100%', border: 'none', outline: 'none', fontSize: 14, padding: 0, fontFamily: 'inherit' }}
                    />
                  </div>
                  <div style={{ width: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div
                      onClick={() => idx > 0 && moveItem(idx, -1)}
                      style={{ cursor: idx > 0 ? 'pointer' : 'default', padding: 2, opacity: idx > 0 ? 1 : 0.3 }}
                    >
                      <ArrowUpIcon size={16} color="#3B82F6" />
                    </div>
                    <div
                      onClick={() => idx < items.length - 1 && moveItem(idx, 1)}
                      style={{ cursor: idx < items.length - 1 ? 'pointer' : 'default', padding: 2, opacity: idx < items.length - 1 ? 1 : 0.3 }}
                    >
                      <ArrowDownIcon size={16} color="#3B82F6" />
                    </div>
                    {items.length > 1 && (
                      <div onClick={() => removeRow(idx)} style={{ cursor: 'pointer', padding: 2 }}>
                        <DeleteIcon size={16} color="#EF4444" />
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div
                onClick={addRow}
                style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#3B82F6' }}
              >
                <AddIcon size={16} color="#3B82F6" />
                <text.T14 style={{ color: '#3B82F6' }}>{te?.addRow || 'Tambah Baris'}</text.T14>
              </div>
            </div>
          </div>

          {/* Total */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', backgroundColor: '#F0FDF4', borderRadius: 10, border: '1px solid #BBF7D0' }}>
            <text.T16 style={{ fontWeight: '700', color: '#065F46' }}>Total</text.T16>
            <text.T16 style={{ fontWeight: '700', color: '#059669' }}>Rp {total.toLocaleString('id-ID')}</text.T16>
          </div>

        </div>
      </main>

      {/* Submit button fixed at bottom */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, maxWidth: 650, margin: '0 auto', padding: '12px 16px', backgroundColor: '#fff', borderTop: '1px solid #F3F4F6', display: 'flex', gap: 10 }}>
        {/* Draft hanya selama belum diajukan — pengajuan pending tidak bisa ditarik jadi draft */}
        {isDraftDoc && (
          <components.Button
            title={submitting ? '...' : (te?.saveDraft || 'Simpan Draft')}
            onClick={submitting ? undefined : () => handleSubmit('draft')}
            colorScheme="light"
            containerStyle={{ flex: 1, opacity: submitting ? 0.6 : 1 }}
          />
        )}
        <components.Button
          title={submitting ? (t.common.submitting || 'Mengirim...') : (te?.submit || 'Ajukan')}
          onClick={submitting ? undefined : () => handleSubmit('pending')}
          containerStyle={{ flex: 2, opacity: submitting ? 0.6 : 1 }}
        />
      </div>
    </div>
  );
};
